import { useMutation, useQuery } from '@hooks/reactQuery';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	readNotificationsFromList,
	removeNotificationFromList,
	removeNotificationsFromList,
	setIsLoadingNotifications,
	setNotifications,
	setNotificationsCount,
	setUnreadNotificationsCount,
} from '@slices/notifications';

import {
	deleteAllNotifications,
	deleteNotification,
	getAllNotifications,
	getNotificationsCount,
	readNotifications,
} from '@api/notifications';

import { ICount } from '@interfaces/common/common';

import { refetchOptions } from '@constants/common';
import { NOTIFICATIONS } from '@constants/queryKeys';

import { INotification } from '../interfaces/notifications/notifications';

export const useNotifications = () => {
	const dispatch = useAppDispatch();

	const { activeOrganisation } = useAppSelector(auth);

	const { mutate: getNotifications } = useMutation({
		mutationKey: [NOTIFICATIONS],
		mutationFn: (meta: { offset: number }) => {
			dispatch(setIsLoadingNotifications({ isLoading: true }));

			getAllNotifications(meta).then(data => {
				const filteredNotifications =  (data as any)?.filter(
					(notification: INotification) => notification.organizationId === activeOrganisation);

				dispatch(setNotifications({
					notifications: filteredNotifications,
				}));
			}).catch(() => {
				dispatch(setIsLoadingNotifications({ isLoading: false }));
			});

			getNotificationsCount(false).then((data: any) => {
				dispatch(setNotificationsCount({ notificationsCount: data?.count }));
			});
		},
		...refetchOptions,
	});

	const { mutate: removeNotification } = useMutation({
		mutationKey: [NOTIFICATIONS],
		mutationFn: (id: string) => {
			return deleteNotification(id).then(() => {
				dispatch(removeNotificationFromList({ id }));
			});
		},
		...refetchOptions,
	});

	const { mutate: readAllNotifications } = useMutation({
		mutationKey: [NOTIFICATIONS],
		mutationFn: () => {
			return readNotifications().then(() => {
				dispatch(readNotificationsFromList({ id: activeOrganisation! }));
			});
		},
		...refetchOptions,
	});

	const { mutate: removeAllNotifications } = useMutation({
		mutationKey: [NOTIFICATIONS],
		mutationFn: () => {
			return deleteAllNotifications().then(() => {
				dispatch(removeNotificationsFromList());
			});
		},
		...refetchOptions,
	});

	useQuery({
			queryKey: [NOTIFICATIONS],
			queryFn: () => getNotificationsCount(true),
			onSuccess: (data: ICount) => {
				dispatch(setUnreadNotificationsCount({ unreadNotificationsCount: data.count }));
			},
			...refetchOptions,
		},
	);

	return {
		getNotifications,
		removeNotification,
		readAllNotifications,
		removeAllNotifications,
	};
};
